/*
  FATMAN FILMS
  FINAL HEADER JS
  - Keeps your existing Fatman mobile drawer
  - Adds custom desktop EN / VI
  - Hides Shopify/Horizon native language dropdown
  - Uses Shopify localization correctly on desktop + mobile
*/

(() => {
  const HEADER_SELECTOR = "#header-component";

  const MASCOT_URL =
    "https://cdn.shopify.com/s/files/1/0788/3715/4014/files/flav-Light-400pxx400px.png?v=1785730411";

  function currentLanguage() {
    return (
      document.documentElement.lang
        ?.slice(0, 2)
        .toLowerCase() || "en"
    );
  }

  function currentReturnTo() {
    return (
      window.location.pathname +
      window.location.search +
      window.location.hash
    );
  }

  function createHiddenInput(name, value) {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = name;
    input.value = value;

    return input;
  }

  function submitLanguage(code) {
    const target = String(code || "")
      .slice(0, 2)
      .toLowerCase();

    if (
      !["en", "vi"].includes(target) ||
      target === currentLanguage()
    ) {
      return;
    }

    const form = document.createElement("form");

    form.method = "post";
    form.action = "/localization";
    form.hidden = true;
    form.setAttribute("accept-charset", "UTF-8");

    form.append(
      createHiddenInput("form_type", "localization"),
      createHiddenInput("utf8", "✓"),
      createHiddenInput("_method", "put"),
      createHiddenInput("language_code", target),
      createHiddenInput("return_to", currentReturnTo())
    );

    document.body.append(form);
    form.submit();
  }

  function injectLanguageStyles() {
    if (document.getElementById("fm-language-switch-styles")) {
      return;
    }

    const style = document.createElement("style");

    style.id = "fm-language-switch-styles";

    style.textContent = `
      /* Hide Shopify / Horizon native localization UI */
      #header-component .dropdown-localization,
      #header-component dropdown-localization-component,
      #header-component .localization-wrapper {
        display: none !important;
      }

      /* Our desktop EN / VI */
      .fm-desktop-language-item {
        display: none;
      }

      @media screen and (min-width: 750px) {
        .fm-desktop-language-item {
          display: inline-flex !important;
          align-items: center;
          margin-left: 4px;
          padding-left: 20px;
          border-left: 1px solid rgba(255, 255, 255, 0.18);
          list-style: none;
        }

        body:not(.template-index) .fm-desktop-language-item,
        #header-component[data-sticky-state="active"] .fm-desktop-language-item {
          border-left-color: rgba(11, 11, 12, 0.16);
        }

        .fm-desktop-language {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .fm-desktop-language__button {
          appearance: none;
          -webkit-appearance: none;
          margin: 0;
          padding: 13px 0;
          border: 0;
          background: transparent;
          color: currentColor;
          font: inherit;
          font-size: 10px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.10em;
          cursor: pointer;
          opacity: 0.46;
          transition:
            opacity 180ms ease,
            color 180ms ease;
        }

        .fm-desktop-language__button.is-active {
          color: var(--fm-header-red, #d90000) !important;
          opacity: 1;
          cursor: default;
        }

        .fm-desktop-language__button:not(.is-active):hover,
        .fm-desktop-language__button:not(.is-active):focus-visible {
          opacity: 1;
        }

        .fm-desktop-language__divider {
          color: currentColor;
          font-family: "Courier New", Courier, monospace;
          font-size: 8px;
          opacity: 0.24;
        }
      }
    `;

    document.head.append(style);
  }

  function createDesktopLanguageButton(code, label) {
    const button = document.createElement("button");

    button.type = "button";
    button.className = "fm-desktop-language__button";
    button.textContent = label;

    button.setAttribute(
      "aria-label",
      `Change language to ${label}`
    );

    if (currentLanguage() === code) {
      button.classList.add("is-active");
      button.setAttribute("aria-current", "true");
    } else {
      button.addEventListener("click", () => {
        submitLanguage(code);
      });
    }

    return button;
  }

  function ensureDesktopLanguageSwitch(desktopMenu) {
    if (
      !desktopMenu ||
      desktopMenu.querySelector(".fm-desktop-language-item")
    ) {
      return;
    }

    const item = document.createElement("li");
    item.className = "fm-desktop-language-item";

    const language = document.createElement("div");
    language.className = "fm-desktop-language";
    language.setAttribute("aria-label", "Language selector");

    const divider = document.createElement("span");
    divider.className = "fm-desktop-language__divider";
    divider.textContent = "/";
    divider.setAttribute("aria-hidden", "true");

    language.append(
      createDesktopLanguageButton("en", "EN"),
      divider,
      createDesktopLanguageButton("vi", "VI")
    );

    item.append(language);
    desktopMenu.append(item);
  }

  function initFatmanHeader() {
    const header = document.querySelector(HEADER_SELECTOR);

    if (!header) {
      return;
    }

    injectLanguageStyles();

    function findMobileMenuToggle() {
      return (
        header.querySelector(
          "#Details-menu-drawer-container > summary"
        ) ||
        header.querySelector(
          "details-menu-drawer summary"
        ) ||
        header.querySelector(
          "menu-drawer summary"
        ) ||
        header.querySelector(
          "summary.header__icon--menu"
        ) ||
        header.querySelector(
          ".header__icon--menu"
        )
      );
    }

    function findDesktopMenu() {
      return Array.from(
        header.querySelectorAll(".menu-list")
      ).find((menu) => {
        return !menu.closest(".menu-drawer");
      });
    }

    function markHeaderLayout() {
      const logo = header.querySelector(".header-logo");

      if (logo) {
        logo.classList.add("fm-header-mobile-logo");
      }

      const menuToggle = findMobileMenuToggle();

      if (menuToggle) {
        menuToggle.classList.add("fm-production-menu-toggle");

        menuToggle.setAttribute(
          "aria-label",
          "Open Fatman Films navigation"
        );
      }

      const desktopMenu = findDesktopMenu();

      if (desktopMenu) {
        const navigationWrapper =
          desktopMenu.closest(
            "nav, header-menu, .header-menu, .overflow-menu"
          ) || desktopMenu;

        navigationWrapper.classList.add(
          "fm-header-desktop-nav"
        );

        ensureDesktopLanguageSwitch(desktopMenu);
      }
    }

    function getDrawerDetails() {
      const mainDrawer = header.querySelector(
        "#Details-menu-drawer-container"
      );

      if (
        mainDrawer &&
        mainDrawer.hasAttribute("open")
      ) {
        return mainDrawer;
      }

      return (
        header.querySelector(
          "details-menu-drawer details[open]"
        ) ||
        header.querySelector(
          "menu-drawer details[open]"
        ) ||
        header.querySelector(
          ".menu-drawer-container[open]"
        )
      );
    }

    function getNavigation() {
      const details = getDrawerDetails();

      if (!details) {
        return null;
      }

      return (
        details.querySelector(".menu-drawer__navigation") ||
        details.querySelector(".menu-drawer")
      );
    }

    function getMenu() {
      const navigation = getNavigation();

      if (!navigation) {
        return null;
      }

      return (
        navigation.querySelector(".menu-drawer__menu") ||
        navigation.querySelector(".menu-list") ||
        navigation.querySelector("ul")
      );
    }

    function closeDrawer() {
      const details = getDrawerDetails();

      if (!details) {
        return;
      }

      const summary =
        details.querySelector(":scope > summary") ||
        details.querySelector("summary");

      if (summary) {
        summary.click();
      } else {
        details.removeAttribute("open");
        document.body.classList.remove(
          "fm-header-menu-open"
        );
      }
    }

    function createLanguageButton(code, label) {
      const wrapper = document.createElement("div");

      wrapper.className = "fm-mobile-language__form";

      const button = document.createElement("button");

      button.type = "button";
      button.className = "fm-mobile-language__button";
      button.textContent = label;

      button.setAttribute(
        "aria-label",
        `Change language to ${label}`
      );

      if (currentLanguage() === code) {
        button.classList.add("is-active");
        button.setAttribute("aria-current", "true");
      }

      button.addEventListener("click", () => {
        if (code === currentLanguage()) {
          return;
        }

        submitLanguage(code);
      });

      wrapper.append(button);

      return wrapper;
    }

    function createTopbar() {
      const topbar = document.createElement("div");

      topbar.className = "fm-mobile-topbar";
      topbar.dataset.fmTopbar = "true";

      const language = document.createElement("div");

      language.className = "fm-mobile-language";
      language.setAttribute(
        "aria-label",
        "Language selector"
      );

      language.append(
        createLanguageButton("en", "EN"),
        createLanguageButton("vi", "VI")
      );

      const close = document.createElement("button");

      close.type = "button";
      close.className = "fm-mobile-close";
      close.setAttribute(
        "aria-label",
        "Close menu"
      );
      close.textContent = "×";

      close.addEventListener(
        "click",
        closeDrawer
      );

      topbar.append(language, close);

      return topbar;
    }

    function createAuthority() {
      const authority = document.createElement("div");

      authority.className = "fm-mobile-authority";

      const dot = document.createElement("span");

      dot.className = "fm-mobile-authority__dot";
      dot.setAttribute("aria-hidden", "true");

      const copy = document.createElement("span");

      copy.className = "fm-mobile-authority__copy";

      const brand = document.createElement("span");

      brand.className = "fm-mobile-authority__brand";
      brand.textContent = "FATMAN FILMS © 2026";

      const location = document.createElement("span");

      location.className =
        "fm-mobile-authority__location";

      location.textContent =
        "HO CHI MINH CITY, VIETNAM";

      copy.append(brand, location);
      authority.append(dot, copy);

      return authority;
    }

    function createStaticMascot() {
      const mascot = document.createElement("div");

      mascot.className = "fm-mobile-mascot";
      mascot.setAttribute("aria-hidden", "true");

      const image = document.createElement("img");

      image.src = MASCOT_URL;
      image.alt = "";
      image.width = 120;
      image.height = 120;
      image.loading = "eager";

      mascot.append(image);

      return mascot;
    }

    function createDrawerFooter() {
      const footer = document.createElement("div");

      footer.className = "fm-mobile-drawer-footer";
      footer.dataset.fmDrawerFooter = "true";

      footer.append(
        createAuthority(),
        createStaticMascot()
      );

      return footer;
    }

    function injectDrawerExtras() {
      const navigation = getNavigation();
      const menu = getMenu();

      if (!navigation || !menu) {
        return;
      }

      if (
        !navigation.querySelector("[data-fm-topbar]")
      ) {
        navigation.insertBefore(
          createTopbar(),
          navigation.firstChild
        );
      }

      if (
        !navigation.querySelector(
          "[data-fm-drawer-footer]"
        )
      ) {
        menu.insertAdjacentElement(
          "afterend",
          createDrawerFooter()
        );
      }
    }

    function updateDrawerState() {
      markHeaderLayout();

      const isOpen = Boolean(
        getDrawerDetails()
      );

      document.body.classList.toggle(
        "fm-header-menu-open",
        isOpen
      );

      if (isOpen) {
        requestAnimationFrame(injectDrawerExtras);

        window.setTimeout(
          injectDrawerExtras,
          80
        );
      }
    }

    markHeaderLayout();
    updateDrawerState();

    if (
      header.dataset.fmHeaderObserverReady !== "true"
    ) {
      header.dataset.fmHeaderObserverReady = "true";

      const observer = new MutationObserver(
        updateDrawerState
      );

      observer.observe(header, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          "open",
          "aria-expanded"
        ]
      });
    }
  }

  document.addEventListener(
    "DOMContentLoaded",
    initFatmanHeader
  );

  document.addEventListener(
    "shopify:section:load",
    initFatmanHeader
  );

  document.addEventListener(
    "shopify:section:reorder",
    initFatmanHeader
  );

  if (document.readyState !== "loading") {
    initFatmanHeader();
  }
})();